/* eslint-disable max-lines */
import { action, observable, computed } from 'mobx';

import CookieStorageService from 'services/CookieStorageService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  BROADCASTER_ASPECT_RATION_SET_RESPONSE,
  BROADCASTER_DEVICES_RECEIVED,
  BROADCASTING_IN_HD,
  END_BROADCAST_ERROR,
  INIT_ROOM,
  INIT_STREAMING_ROOM_RESPONSE,
  MIC_BLOCKED,
  MIC_LEVEL_VALUE,
  MIC_NOT_FOUND,
  PAUSE_STATUS_SUCCESS,
  PRIVATE_ROOM_RESPONSE,
  ROOM_LOADING_STATUS,
  SET_BROADCASTER_VIDEO_LOADING,
  SET_DEVICE_ERROR,
  SET_FPS_FROM_JINK,
  SET_STREAMING_JWT_TOKEN,
  SHOULD_REINIT_BROADCAST,
  SHOW_ENDED,
  START_BROADCAST_RESPONSE,
  STREAM_KEY_RESPONSE,
  TIPPERS_LEADERBOARD_DATA,
  UPDATE_AUDIO_DEVICE,
  UPDATE_VIDEO_DEVICE,
  USER_BROADCAST_SETTINGS_ERROR,
  USER_BROADCAST_SETTINGS_RESPONSE,
  USER_HAS_MULTIPLE_IDENTITIES,
} from 'services/MessagingService/EventNames';
import ModalService from 'services/ModalService';
import StreamingService from 'services/StreamingService';
import PlayerService from 'services/Streaming/PlayerService';
import BroadcasterStore from 'stores/BroadcasterStore';
import BroadcasterPrivateShowStore from 'stores/BroadcasterPrivateShowStore';
import ChatStore from 'stores/ChatStore';
import TagsStore from 'stores/TagsStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { ROOM_MODE } from 'enum/profile';
import allUserGroups from 'enum/userGroups';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { BROADCAST_TYPE } from 'enum/videoPlayer';
import {
  STREAM_QUALITY,
  STREAM_SOURCE_EXTERNAL,
  STREAM_SOURCE_WEBRTC,
  STREAM_PROVIDERS,
  STREAM_PROTOCOL,
} from 'utils/consts';


const GO_LIVE_DELAY_SECONDS = 15;
const GO_LIVE_DELAY_MS = GO_LIVE_DELAY_SECONDS * SECOND_AS_MILLISECONDS;

class StreamingStore {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(USER_BROADCAST_SETTINGS_RESPONSE, this._handleBroadcastSettingsResponse);
    this.messagingService.on(USER_BROADCAST_SETTINGS_ERROR, this._handleBroadcastSettingsError);
    this.messagingService.on(BROADCASTER_DEVICES_RECEIVED, this._handleDevicesReceived);
    this.messagingService.on(INIT_STREAMING_ROOM_RESPONSE, this._handleInitStreamingRoom);
    this.messagingService.on(BROADCASTER_ASPECT_RATION_SET_RESPONSE, this.setIsPrefferedAspectRatio);
    this.messagingService.on(SET_FPS_FROM_JINK, this._handleFpsFromJink);
    this.messagingService.on(SET_BROADCASTER_VIDEO_LOADING, this.setIsVideoLoadingVisible);
    this.messagingService.on(MIC_LEVEL_VALUE, this.handleMicVolume);
    this.messagingService.on(USER_HAS_MULTIPLE_IDENTITIES, this.handleUserIdentities);
    this.messagingService.on(BROADCASTING_IN_HD, this.setHDStreaming);
    this.messagingService.on(START_BROADCAST_RESPONSE, this._handleStartBroadcastResponse);
    this.messagingService.on(STREAM_KEY_RESPONSE, this._handleStreamKeyResponse);
    this.messagingService.on(TIPPERS_LEADERBOARD_DATA, this.handleTopTippers);
    this.messagingService.on(SHOW_ENDED, this._handleShowEnded);
    this.messagingService.on(ROOM_LOADING_STATUS, this.handleRoomStatus);
    this.messagingService.on(SET_DEVICE_ERROR, this.handleDeviceError);
    this.messagingService.on(PAUSE_STATUS_SUCCESS, this.handlePauseSuccess);
    this.messagingService.on(MIC_BLOCKED, this.handleMicBlocked);
    this.messagingService.on(SHOULD_REINIT_BROADCAST, this.handleBroadcastReinit);
    this.messagingService.on(UPDATE_VIDEO_DEVICE, this.updateVideoDevice);
    this.messagingService.on(UPDATE_AUDIO_DEVICE, this.updateAudioDevice);
    this.messagingService.on(SET_STREAMING_JWT_TOKEN, this.setStreamingJwtToken);
    this.messagingService.on(MIC_NOT_FOUND, this.handleMicNotFound);
    this.messagingService.on(PRIVATE_ROOM_RESPONSE, this.setPrivateRoomMode);
  }

  destructor() {
    this.messagingService.off(END_BROADCAST_ERROR, this._handleBroadcastEndError);
    this.messagingService.off(USER_BROADCAST_SETTINGS_RESPONSE, this._handleBroadcastSettingsResponse);
    this.messagingService.off(USER_BROADCAST_SETTINGS_ERROR, this._handleBroadcastSettingsError);
    this.messagingService.off(BROADCASTER_DEVICES_RECEIVED, this._handleDevicesReceived);
    this.messagingService.off(INIT_STREAMING_ROOM_RESPONSE, this._handleInitStreamingRoom);
    this.messagingService.off(BROADCASTER_ASPECT_RATION_SET_RESPONSE, this.setIsPrefferedAspectRatio);
    this.messagingService.off(SET_FPS_FROM_JINK, this._handleFpsFromJink);
    this.messagingService.off(SET_BROADCASTER_VIDEO_LOADING, this.setIsVideoLoadingVisible);
    this.messagingService.off(MIC_LEVEL_VALUE, this.handleMicVolume);
    this.messagingService.off(USER_HAS_MULTIPLE_IDENTITIES, this.handleUserIdentities);
    this.messagingService.off(BROADCASTING_IN_HD, this.setHDStreaming);
    this.messagingService.off(START_BROADCAST_RESPONSE, this._handleStartBroadcastResponse);
    this.messagingService.off(STREAM_KEY_RESPONSE, this._handleStreamKeyResponse);
    this.messagingService.off(TIPPERS_LEADERBOARD_DATA, this.handleTopTippers);
    this.messagingService.off(SHOW_ENDED, this._handleShowEnded);
    this.messagingService.off(ROOM_LOADING_STATUS, this.handleRoomStatus);
    this.messagingService.off(SET_DEVICE_ERROR, this.handleDeviceError);
    this.messagingService.off(PAUSE_STATUS_SUCCESS, this.handlePauseSuccess);
    this.messagingService.off(MIC_BLOCKED, this.handleMicBlocked);
    this.messagingService.off(SHOULD_REINIT_BROADCAST, this.handleBroadcastReinit);
    this.messagingService.off(UPDATE_VIDEO_DEVICE, this.updateVideoDevice);
    this.messagingService.off(UPDATE_AUDIO_DEVICE, this.updateAudioDevice);
    this.messagingService.off(SET_STREAMING_JWT_TOKEN, this.setStreamingJwtToken);
    this.messagingService.off(MIC_NOT_FOUND, this.handleMicNotFound);
    this.messagingService.off(PRIVATE_ROOM_RESPONSE, this.setPrivateRoomMode);
  }

  @observable broadcastSettings = { streamSource: null };
  @observable isStreamLive = false;
  @observable requestBroadcastEndStatus = APIStates.INITIAL;
  @observable getBroadcastSettingsStatus = APIStates.INITIAL;
  @observable devicesReceived = [];
  @observable initStreamingRoomData = {};
  @observable streamingObject = {};
  @observable currentAudioDevice = '';
  @observable currentVideoDevice = '';
  @observable isPrefferedAspectRatio = true;
  @observable isMirrorStream = false;
  @observable streamFps = 0;
  @observable broadcastType = BROADCAST_TYPE.SOLO;
  @observable privateRoom = false;
  @observable privateRoomPassword = '';
  @observable isLoadingStreamingVideo = true;
  @observable broadcastId = null;
  @observable micVolume = 0;
  @observable goLiveBlocked = true;
  @observable userHasMultipleIdentities = false;
  @observable isHd = false;
  @observable hasGoneLive = false;
  @observable streamKeyData = { streamKey: '' };
  @observable introductionTutorialFlag = false;
  @observable streamingJwtToken = '';
  @observable isBroadcastOver = false;
  @observable startBroadcastStatus = false;


  @observable originInfo = {
    server: '',
    originId: null,
  };

  @observable streamUUID = null;
  @observable topTippers = [];
  @observable externalStreamFps = 0;
  @observable audioMuted = false;
  @observable roomLoadingStatus = APIStates.INITIAL;
  @observable deviceError = '';
  @observable showExternalStreamPreview = true;
  @observable usedExternalEncoderBefore = false;
  @observable pauseStreamLoadingStatus = APIStates.INITIAL;
  @observable isVideoStatsVisible = false;
  @observable isMicBlocked = false;
  @observable goLiveDelayed = false;
  @observable broadcastedOnBwr = false;
  @observable micNotFound = false;
  @observable isDisconnected = false;

  @action setIsStreamLive = (live) => {
    this.isStreamLive = live;
  };

  @action setPrivateRoomMode = (data) => {
    const { username, accessHash, privateMode } = data;
    this.privateRoom = privateMode;
    this.setBroadcastSettings(username, accessHash);
  };

  /**
   * Method for updating the stream paused status
   * @param {String} username - the roomname, should be equal with the logged in user
   * @param {String} accessHash - the accessHash of the logged in user
   * @param {String} action - the action to be performed, can be PAUSE or RESUME
   */
  @action updatePauseStatus = (username, accessHash, actionType) => {
    this.pauseStreamLoadingStatus = APIStates.PENDING;
    StreamingService.updatePauseStatus(username, accessHash, actionType);
  };

  @action handlePauseSuccess = () => {
    this.pauseStreamLoadingStatus = APIStates.LOADED;
  };

  @action handleMicNotFound = () => {
    this.micNotFound = true;
  };

  /**
   * Method for setting stream source
   * @param {String} source - stream source
   */
  @action setStreamSource = (source) => {
    this.broadcastSettings = Object.assign(this.broadcastSettings, { streamSource: source });
  };

  /**
   * Method for setting show details category (solo, mf couple, female couple)
   * @param {String} broadcastType
   */
  @action setBroadcastType = (broadcastType) => {
    this.broadcastType = broadcastType;
    const isGroupBroadcast =
      broadcastType === BROADCAST_TYPE.FEMALE_GROUP ||
      broadcastType === BROADCAST_TYPE.MALE_FEMALE_GROUP ||
      broadcastType === BROADCAST_TYPE.MALE_GROUP;
    if (!this.userHasMultipleIdentities && isGroupBroadcast) {
      ModalService.showModal(ModalService.MODAL_KEYS.INFORM_MULTIPLE_IDENTITIES_ONBOARDING_MODAL, {
        close: ModalService.hideModal,
        show: true,
      });
    }
  };

  /**
   * Method for setting show details room mode (public/private)
   * @param {Boolean} privateRoom
   */
  @action setPrivateRoom = (privateRoom) => {
    if (privateRoom === ROOM_MODE.PUBLIC) {
      this.privateRoom = false;
    } else {
      this.privateRoom = true;
    }
  };

  /**
   * Method for setting show details private room password
   * @param {String} privateRoomPassword
   */
  @action setPrivateRoomPassword = (privateRoomPassword) => {
    this.privateRoomPassword = privateRoomPassword;
  };

  /**
   * Method for handling broadcast end error
   */
  @action _handleBroadcastEndError = () => {
    this.requestBroadcastEndStatus = APIStates.ERROR;
  };

  @action resetStream = (isCeeBlue, needsReinit) => {
    if (this.isWebrtc) {
      StreamingService.stopPushAndResetClientMedia(isCeeBlue, needsReinit);
    }
  };

  /**
   * Private method for handling broadcast settings response
   * @param {Object} data - data from api response
   */
  @action _handleBroadcastSettingsResponse = (data) => {
    this.broadcastSettings = data;
    this.currentAudioDevice = data.mic;
    this.currentVideoDevice = data.camera;
    this.broadcastType = data.details.broadcastType;
    this.isMirrorStream = data.details.mirrorStream ? data.details.mirrorStream : false;
    this.privateRoom = data.details.privateRoom;
    this.privateRoomPassword = data.details.password;
    this.getBroadcastSettingsStatus = APIStates.LOADED;
    this.usedExternalEncoderBefore = data.details?.usedExternalEncoderBefore;
    this.broadcastedOnBwr = data.details?.broadcastedOnBwr;

    if (data.streamSource === STREAM_SOURCE_WEBRTC && data.details.technology === 'rtmp') {
      if (!CookieStorageService.get('obs_warning_30_days') && !CookieStorageService.get('obs_warning_24_hours')) {
        ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_OBS_VIRTUAL_CAMERA_WARNING_MODAL);
      }
    }
  };

  @action updateVideoDevice = (deviceId) => {
    this.currentVideoDevice = deviceId;
  };

  @action updateAudioDevice = (deviceId) => {
    this.currentAudioDevice = deviceId;
  };

  @action setBroadcastedOnBwr = () => {
    this.broadcastedOnBwr = true;
  };

  /**
   * Private method for handling show ended
   */
  @action _handleShowEnded = ({ roomname }) => {
    if (!this.isWebrtc) {
      window.location.reload();

      return;
    }

    if (this.broadcastId) {
      this.setBroadcastOver(true);
      ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL);

      return;
    }
    const roomNameLowercased = roomname?.toLowerCase();
    this.requestBroadcastEndStatus = APIStates.LOADED;
    this.broadcastId = null;
    this.hasGoneLive = false;
    this.streamFps = 0;
    ChatStore.resetChatStoreState();
    ChatStore.removeChatListeners(roomNameLowercased, roomNameLowercased);
    BroadcasterStore.clearState();
    BroadcasterPrivateShowStore.resetPrivateShowRequests();
  };

  /**
   * Private method for handling broadcast settings error
   */
  @action _handleBroadcastSettingsError = () => {
    this.getBroadcastSettingsStatus = APIStates.ERROR;
  };

  /**
   * Private method for handling device error
   */
  @action handleDeviceError = (error) => {
    this.deviceError = error;
    this.isLoadingStreamingVideo = false;
  };

  /**
   * Private method for handling broadcaster devices received
   */
  @action _handleDevicesReceived = (data) => {
    this.devicesReceived = data;
  };

  /**
   * Method for setting if we broadcaster can use 16:9 or 4:3
   */
  @action setIsPrefferedAspectRatio = (data) => {
    this.isPrefferedAspectRatio = data;
  };

  /**
   * Private method for handling broadcaster initalization streaming room response
   */
  @action _handleInitStreamingRoom = (data) => {
    this.initStreamingRoomData = data;
  };

  /**
   * Method for setting is video loading
   */
  @action setIsVideoLoadingVisible = (value) => {
    this.isLoadingStreamingVideo = value;
  };

  /**
   * Method for rendering mic volume value
   */
  @action handleMicVolume = (value) => {
    this.micVolume = value;
  };

  @action handleUserIdentities = (value) => {
    this.userHasMultipleIdentities = value;
  };

  @action setHDStreaming = (value) => {
    this.isHd = value;
  };

  /**
   * Method for settings fps for external encoder stream
   */
  @action setExternalStreamFps = (fps) => {
    this.externalStreamFps = fps;
    this.setGoLiveBlocked(!this.isEnoughFpsToGoLive);
  };

  /**
   * Method for toggle mirror stream
   */
  @action toggleMirrorStream = () => {
    this.isMirrorStream = !this.isMirrorStream;
  };

  /**
   * Method for toggling external stream preview
   */
  @action toggleShowExternalStreamPreview = () => {
    this.showExternalStreamPreview = !this.showExternalStreamPreview;
  };

  /**
   * Method to set current audio device
   */
  @action handleBroadcasterAudioDevice = (event) => {
    this.currentAudioDevice = event.target.value;
    StreamingService.setMicChange(event.target.value, this.currentVideoDevice);
    this.setGoLiveBlocked(true);
  };

  handleInTranslationClick = () => {
    this.setStreamSource(STREAM_SOURCE_EXTERNAL);
    ModalService.hideModal();
  };

  /**
   * Method to set current video device
   */
  @action handleBroadcasterVideoDevice = (event, hasCFOBSWarning) => {
    if (hasCFOBSWarning) {
      if (event.target.selectedOptions[0].label.includes('OBS')) {
        if (!CookieStorageService.get('obs_warning_24_hours') && !CookieStorageService.get('obs_warning_30_days')) {
          ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_OBS_VIRTUAL_CAMERA_WARNING_MODAL);
        }
      }
    }
    StreamingService.handleCameraChange(
      event.target.value,
      this.deviceError,
      UserStore.hasCF(allUserGroups.STREAMING_SAAS),
      this.currentAudioDevice
    );
    StreamingService.updateSelectedVideoId(event.target.value);
    this.currentVideoDevice = event.target.value;
    this.setGoLiveBlocked(true);
  };

  @action handleTopTippers = (data) => {
    this.topTippers = data.filter((tipper) => tipper.user.indexOf('Guest') === -1);
  };

  /**
   * Method to reset data in jink and destroy jink
   */
  resetStreamDataJink = () => StreamingService.destroyJink();

  /**
   * Method to set fps number
   */
  @action _handleFpsFromJink = (fps) => {
    if (fps !== undefined) {
      this.streamFps = fps;
    }
    this.setGoLiveBlocked(!this.isEnoughFpsToGoLive);
  };

  @action updateTutorialFlag = (flag) => {
    this.introductionTutorialFlag = flag;
  };

  /**
   * Initiate a broadcast end operation that will delete the node in firebase after setting newShowState to SHOW_ENDED
   */
  @action requestBroadcastEnd = async(username, accessHash, isCeeBlue) => {
    this.requestBroadcastEndStatus = APIStates.PENDING;
    this.broadcastId = null;
    const data = this.getBroadcastSettingsData(username);
    await StreamingService.requestEndBroadcast(username, accessHash, data.streamUuid);
    if (!this.showExternalStreamPreview) {
      this.showExternalStreamPreview = true;
    }
    const needsReinit = true;
    this.resetStream(isCeeBlue, needsReinit);
    if (this.audioMuted) {
      this.unmuteAudio();
    }
  };

  /**
   * Method for getting user broadcast settings
   * @param {string} username
   * @param {string} accessHash
   * @emits USER_BROADCAST_SETTINGS_RESPONSE emits the response data for broadcast settings
   */
  @action getUserBroadcastSettings = (username, accessHash) => {
    this.getBroadcastSettingsStatus = APIStates.PENDING;
    StreamingService.getUserBroadcastSettings(username, accessHash);
  };

  /**
   * Method for initializing streaming room
   * @param {string} protocol
   * @param {string} provider
   */
  initStreamingRoom = async(protocol, provider, switchingStreamingProvider = false) => {
    await StreamingService.initStreamingRoom({
      protocol,
      provider,
      sourceType: this.broadcastSettings.streamSource,
      switchingStreamingProvider,
    });
  };

  /**
   * Method to set if broadcaster can go live
   * @param {Boolean} goLiveBlocked - wether the broadcaster can go live or not
   */
  @action setGoLiveBlocked = (goLiveBlocked) => {
    this.goLiveBlocked = goLiveBlocked;
  };

  /**
   * Method for initializing wowza external encoder room
   */
  initWowzaStreamingRoom = () => {
    this.initStreamingRoom(STREAM_PROTOCOL.RTMP, STREAM_PROVIDERS.WOWZA);
  };

  /**
   * Method for muting audio
   */
  @action muteAudio = () => {
    this.audioMuted = true;
    StreamingService.muteAudio();
  };

  /**
   * Method for unmuting audio
   */
  @action unmuteAudio = () => {
    this.audioMuted = false;
    StreamingService.unmuteAudio();
  };

  /**
   * Method for setting room loading to false
   */
  @action handleRoomStatus = (status) => {
    this.roomLoadingStatus = status;
  };

  getVideoDimensions = () => {
    let width = this.broadcastSettings.width || 0;
    let height = this.broadcastSettings.height || 0;

    const metricsResolution = PlayerService?.videoMetrics?.resolution || '';
    const [newWidth, newHeight] = metricsResolution.replace('undefined', '').split('x');

    if (newWidth && newHeight) {
      width = parseInt(newWidth, 10);
      height = parseInt(newHeight, 10);
    }

    return [width, height];
  };

  getBroadcastSettingsData = (username) => {
    const isViewerBroadcast = CookieStorageService.get('isViewerBroadcast');
    const [videoWidth, videoHeight] = this.getVideoDimensions();
    let data = {
      streamUuid: this.initStreamingRoomData.id && this.initStreamingRoomData.id.streamUuid,
      width: videoWidth,
      height: videoHeight,
      statusMessage: this.statusTagsMessage,
      broadcastType: this.broadcastType,
      privateRoom: this.privateRoom,
      roomName: username,
      streamSource: this.broadcastSettings.streamSource,
      password: this.privateRoomPassword,
      listInDirectory: !isViewerBroadcast,
      cameraName: this.currentVideoDevice,
      microphoneName: this.currentAudioDevice,
      autoResolution: this.broadcastSettings.autoResolution,
      autoWidth: videoWidth,
      autoHeight: videoHeight,
      aspectRatio: this.isPrefferedAspectRatio ? '16:9' : '4:3',
      qualityFactor: 1,
      streamingProvider: this.initStreamingRoomData.provider,
      useStreamingSaas: STREAM_PROVIDERS.CEEBLUE === this.initStreamingRoomData.provider
      || UserStore.hasCF(allUserGroups.STREAMING_SAAS_WOWZA),
      mirrorStream: this.isMirrorStream,
      broadcastedOnBwr: true,
    };
    if (this.broadcastSettings.streamSource === STREAM_SOURCE_EXTERNAL) {
      data = {
        streamUuid: this.streamUUID,
        originServerId: this.originInfo.originId,
        encoder: this.originInfo.encoder,
        originUrl: this.initStreamingRoomData?.input?.uri,
        width: videoWidth,
        height: videoHeight,
        statusMessage: this.statusTagsMessage,
        broadcastType: this.broadcastType,
        privateRoom: this.privateRoom,
        roomName: username,
        streamSource: STREAM_SOURCE_EXTERNAL,
        password: this.privateRoomPassword,
        listInDirectory: !isViewerBroadcast,
        cameraName: this.broadcastSettings.camera,
        microphoneName: this.broadcastSettings.mic || 'default',
        autoResolution: this.broadcastSettings.autoResolution || false,
        autoWidth: videoWidth,
        autoHeight: videoHeight,
        aspectRatio: this.broadcastSettings.aspectRatio || '16:9',
        technology: 'rtmp',
        usedExternalEncoderBefore: true,
        broadcastedOnBwr: true,
      };
    }

    return data;
  };

  /**
   * Method for starting broadcast
   * @param {string} username
   * @param {string} accessHash
   */
  startBroadcast = async(username, accessHash, isBroadcastRestart) => {
    if (!isBroadcastRestart) {
      this.roomLoadingStatus = APIStates.PENDING;
    }
    const isViewerBroadcast = CookieStorageService.get('isViewerBroadcast');
    const data = this.getBroadcastSettingsData(username);

    await StreamingService.startBroadcast(username, accessHash, data, isViewerBroadcast, isBroadcastRestart);
  };

  @action _handleStartBroadcastResponse = (data) => {
    this.broadcastId = data.broadcastId;
    this.hasGoneLive = true;
    if (!data.isBroadcastRestart) {
      this.messagingService.emit(INIT_ROOM, {
        roomName: data.username.toLowerCase(),
        username: data.username,
        accessHash: data.accessHash,
        password: this.privateRoomPassword,
      });
    }
  };

  onSwitchProvider = async() => {
    let provider = STREAM_PROVIDERS.CEEBLUE;
    if (this.initStreamingRoomData.provider === STREAM_PROVIDERS.CEEBLUE) {
      provider = STREAM_PROVIDERS.WOWZA;
    }
    const { user: { username, accessHash } } = UserStore;

    try {
      StreamingService.triesLeft = 10;
      StreamingService.slowInternetRetriesLeft = 3;
      await this.initStreamingRoom(STREAM_PROTOCOL.WEBRTC, provider, true);
      await this.startBroadcast(username, accessHash, true);
    } catch {
      // move on
    }
  };

  @action handleStartBroadcastStatus = (res) => {
    this.startBroadcastStatus = res;
  };

  setBroadcastSettings = (username, accessHash) => {
    const data = this.getBroadcastSettingsData(username);
    StreamingService.setBroadcasterSettings(data, username, accessHash);
  };

  handleBroadcastReinit = ({ username, accessHash }) => {
    if (this.broadcastId) {
      const isBroadcastRestart = true;
      const TIMEOUT = 2000;
      setTimeout(() => this.startBroadcast(username, accessHash, isBroadcastRestart), TIMEOUT);
    }
  };

  @action _handleStreamKeyResponse = (data) => {
    this.streamKeyData = data;
  };

  @action getUserIdentities = (username) => {
    StreamingService.getUserIdentities(username);
  };

  @action setStreamingJwtToken = (token) => {
    this.streamingJwtToken = token;
  };

  @action setRoomDisconnected = (isDisconnected) => {
    this.isDisconnected = isDisconnected;
  };

  @action watchExternalEncoderSync = (username, accessHash) => {
    StreamingService.externalNodeChildAdded(username, (snapshot) => {
      this.originInfo = snapshot.val();
      this.streamUUID = snapshot.key();
      if (this.broadcastId) {
        const isBroadcastRestart = true;
        this.startBroadcast(username, accessHash, isBroadcastRestart);
      }
    });

    StreamingService.externalNodeChildRemoved(username, () => {
      this.originInfo = {};
      this.externalStreamFps = 0;
      this.setHDStreaming(false);
    });
  };

  @action resetOriginInfo = () => {
    this.originInfo = {};
  };

  /**
   * Method for getting stream key
   * @param {string} username
   * @param {string} accessHash
   */
  getStreamKey = (username, accessHash) => {
    StreamingService.getStreamKey(username, accessHash);
  };

  /* @method getter isEnoughFpsToGoLive - check if the stream quality is good enough to start stream
   * @returns boolean
   */
  @computed get isEnoughFpsToGoLive() {
    if (this.broadcastSettings.streamSource === STREAM_SOURCE_EXTERNAL) {
      return this.externalStreamFps > 3;
    }

    return this.streamFps > 3;
  }

  /**
   * Method for reseting stream key
   * @param {string} username
   * @param {string} accessHash
   */
  resetStreamKey = (username, accessHash) => {
    StreamingService.resetStreamKey(username, accessHash);
  };

  @action resetBroadcastEndStatus = () => {
    this.requestBroadcastEndStatus = APIStates.INITIAL;
  };

  @action toggleVideoStatsVisibility = () => {
    this.isVideoStatsVisible = !this.isVideoStatsVisible;
  };

  @action handleMicBlocked = () => {
    this.isMicBlocked = true;
  };

  @action setBroadcastOver = () => {
    this.isBroadcastOver = true;
    this.setRoomDisconnected(true);
  };

  // done to avoid issues with webchat
  @action setGoLiveDelay = () => {
    this.goLiveDelayed = true;
    setTimeout(() => {
      this.goLiveDelayed = false;
    }, GO_LIVE_DELAY_MS);
  };

  @computed get streamPreviewSource() {
    const { server } = this.originInfo;

    if (!server) {
      return null;
    }

    if (UserStore.hasCF(allUserGroups.CAM2CAM_OBS)) {
      const url = new URL(server);
      url.protocol = 'wss';
      url.pathname = 'webrtc-session.json';

      return url.href;
    }

    if (this.streamUUID && this.streamingJwtToken) {
      const url = new URL(`${this.originInfo.server}/${this.streamUUID}/playlist.m3u8`);
      url.protocol = 'https:';
      url.searchParams.set('Authorization', this.streamingJwtToken);
      url.searchParams.set('username', UserStore.user.username);
      url.searchParams.set('timestamp', Date.now());
      url.searchParams.set('referer', document.domain);

      return url.href;
    }

    return null;
  }

  /* @method getter noStreamSource - check if there is no streamSource
   * @returns boolean
   */
  @computed get noStreamSource() {
    return !this.broadcastSettings.streamSource;
  }

  /* @method getter audioDevices - filtering audio devices
   * @returns array
   */
  @computed get audioDevices() {
    return this.devicesReceived && this.devicesReceived.filter((device) => device.kind === 'audioinput');
  }

  /* @method getter videoDevices - filtering video devices
   * @returns array
   */
  @computed get videoDevices() {
    return this.devicesReceived && this.devicesReceived.filter((device) => device.kind === 'videoinput');
  }

  /* @method getter isBroadcastSettingsPending - check if fetching broadcast status is pending
   * @returns boolean
   */
  @computed get broadcastSettingsPending() {
    return this.getBroadcastSettingsStatus === APIStates.PENDING;
  }

  /* @method getter streamQualityFromFps - calculating stream fps and returns quality
   * @returns string
   */
  @computed get streamQualityFromFps() {
    const currentFps = this.isWebrtc ? this.streamFps : this.externalStreamFps;
    const poorFpsUpperLimit = 9;
    const goodFpsUpperLimit = 19;
    switch (true) {
      case currentFps > 0 && currentFps <= poorFpsUpperLimit:
        return STREAM_QUALITY.POOR;
      case currentFps > poorFpsUpperLimit && currentFps <= goodFpsUpperLimit:
        return STREAM_QUALITY.GOOD;
      case currentFps > goodFpsUpperLimit:
        return STREAM_QUALITY.GREAT;
      default:
        return STREAM_QUALITY.NOT_AVAILABLE;
    }
  }

  /* @method getter fps - checks the stream source and returns corresponding fps number
   */
  @computed get fps() {
    const sourceFps = this.isWebrtc ? this.streamFps : this.externalStreamFps;

    return sourceFps > 0 ? sourceFps : 'N/A';
  }

  /* @method getter statusTagsMessage - making string from chosen tags
   * @returns string
   */
  @computed get statusTagsMessage() {
    const tagsString = TagsStore.chosenTags.map((tag) => ` #${tag.name}`).join('');

    return tagsString;
  }

  /* @method getter isWebrtc - check if selected stream source is webrtc
   * @returns boolean
   */
  @computed get isWebrtc() {
    return this.broadcastSettings.streamSource === STREAM_SOURCE_WEBRTC;
  }

  /* @method getter streamQualityGreat - check if stream quality is great
   * @returns boolean
   */
  @computed get isStreamQualityGreat() {
    return this.streamQualityFromFps === STREAM_QUALITY.GREAT;
  }

  /* @method getter canGoLive - check if broadcaster can go live
   * @returns boolean
   */
  @computed get canGoLive() {
    if (this.isStreamLive) {
      return true;
    }

    return !this.goLiveBlocked && this.isEnoughFpsToGoLive && !this.goLiveDelayed;
  }

  @computed get isBroadcastEndPending() {
    return this.requestBroadcastEndStatus === APIStates.PENDING;
  }

  @computed get isBroadcastEnded() {
    return this.requestBroadcastEndStatus === APIStates.LOADED;
  }

  @computed get isRoomLoading() {
    return this.roomLoadingStatus === APIStates.PENDING;
  }

  @computed get isRoomInitFailed() {
    return this.roomLoadingStatus === APIStates.ERROR;
  }

  @computed get isStreamPauseLoading() {
    return this.pauseStreamLoadingStatus === APIStates.PENDING;
  }

  @computed get isRoomDisconnected() {
    return this.isDisconnected;
  }

  @computed get isStreamOver() {
    return this.isBroadcastOver;
  }

}

export default new StreamingStore();
